import React, {ReactNode, useEffect, useState} from 'react'

import L from '../leaflet.js'
import {activeZoneTool, drawsPoints, removeLastPoint, setZoneTool, toggleZoneTool, ZoneTool} from '../zoneTools.js'
import MapControlCard from './MapControlCard.js'
import MapControlButton from './MapControlButton.js'

interface ZoneDrawingControlsProps {
    /** A map set up with setupDrawing (geoman.ts) */
    map: L.Map | null
    /** The tools that get a button, in this order */
    tools: ZoneTool[]
    /** More buttons in the card, after the tools */
    children?: ReactNode
}

const toolButtons: Record<ZoneTool, {icon: string, label: string}> = {
    Polygon: {icon: 'mdi mdi-vector-polygon', label: 'Gebied tekenen'},
    Circle: {icon: 'mdi mdi-vector-circle', label: 'Cirkel tekenen'},
    Edit: {icon: 'mdi mdi-vector-square-edit', label: 'Gebieden bewerken'},
    Cut: {icon: 'mdi mdi-content-cut', label: 'Stuk uit een gebied knippen'}
}

const toolEvents = 'pm:globaldrawmodetoggled pm:globaleditmodetoggled pm:globalcutmodetoggled'

/**
 * The card with the zone tools (geoman) for MapControls: a button per tool that switches it on, or off again, and
 * shows which one is on. Escape also switches it off (exitModeOnEscape). While drawing point by point a button takes
 * back the last point. Switching to another tool, or removing the card, stops the one that is on.
 */
export default function ZoneDrawingControls({map, tools, children}: ZoneDrawingControlsProps) {
    const [active, setActive] = useState<ZoneTool | null>(null)

    useEffect(() => {
        if (!map) {
            return
        }

        // A drawn shape, Escape and the tool buttons all end up here
        const update = () => setActive(activeZoneTool(map.pm))
        // The map is removed before this card when both go: then there is nothing to switch off
        let removed = false
        const onUnload = () => {
            removed = true
        }

        map.on(toolEvents, update)
        map.on('unload', onUnload)
        update()

        return () => {
            map.off(toolEvents, update)
            map.off('unload', onUnload)

            if (!removed) {
                setZoneTool(map.pm, null)
            }
        }
    }, [map])

    return (
        <MapControlCard>
            {tools.map((tool) => (
                <MapControlButton
                    key={tool}
                    icon={toolButtons[tool].icon}
                    label={toolButtons[tool].label}
                    active={active === tool}
                    disabled={!map}
                    onClick={() => map && toggleZoneTool(map.pm, tool)}
                />
            ))}

            {children}

            {drawsPoints(active) && map &&
                <MapControlButton icon='mdi mdi-undo' label='Laatste punt verwijderen' onClick={() => removeLastPoint(map.pm)} />
            }
        </MapControlCard>
    )
}
